import { useEffect, useRef } from 'react';
import { useLogout } from 'react-admin';
import { SESSION_ENDED_EVENT } from '../providers/sessionEvents';
import { StorageKey } from '../providers/storage';

/**
 * XBIT-4826 D10.4: when this session ends anywhere, this tab returns to the sign-in page. Two signals:
 * another tab clearing the stored session (`storage` event), and this tab's own interceptor ending it
 * (SESSION_ENDED_EVENT: screens that call axios directly have no `checkError` to navigate for them).
 * `authProvider.logout` makes no server call here: nothing is stored any more. Signals that arrive while a
 * logout is already running (the dataProvider path navigates through `checkError` too) are dropped.
 */
export default function SessionSync() {
  const logout = useLogout();
  const running = useRef(false);
  useEffect(() => {
    const signOut = () => {
      if (running.current) return;
      running.current = true;
      Promise.resolve(logout()).finally(() => {
        running.current = false;
      });
    };
    const onStorage = (event: StorageEvent) => {
      const cleared = event.key === null || (event.key === StorageKey.AuthData && event.newValue === null);
      if (cleared) signOut();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SESSION_ENDED_EVENT, signOut);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SESSION_ENDED_EVENT, signOut);
    };
  }, [logout]);
  return null;
}
